/**
 * ConvertCaseToLegalProcessModal
 * Modal for converting a case to a legal process
 */

import { useState, useEffect } from 'react';
import { Briefcase, Scale, AlertCircle, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { caseService } from '@/services/case-service';
import type { Case } from '@/types/case';
import toast from '@/lib/toast';

import { getErrorMessage } from '../../utils/error.utils'
export interface ConvertCaseToLegalProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (updatedCase: Case) => void;
  caseData: {
    id: string;
    title: string;
    description?: string;
  } | null;
}

export function ConvertCaseToLegalProcessModal({
  isOpen,
  onClose,
  onSuccess,
  caseData
}: ConvertCaseToLegalProcessModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    courtNumber: '',
    courtName: '',
    judgeName: ''
  });

  // Reset form when modal opens/case changes
  useEffect(() => {
    if (isOpen && caseData) {
      setFormData({
        courtNumber: '',
        courtName: '',
        judgeName: ''
      });
    }
  }, [isOpen, caseData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!caseData || !formData.courtNumber.trim()) {
      toast.error('Número do processo judicial é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const updatedCase = await caseService.convertToLegalProcess(
        caseData.id,
        formData
      );

      toast.success('Caso convertido em Processo Judicial com sucesso!');

      if (onSuccess) {
        onSuccess(updatedCase);
      }

      handleClose();
    } catch (error: unknown) {
      console.error('Error converting case to legal process:', error);
      toast.error(
        getErrorMessage(error, 'Erro ao converter caso em processo judicial')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      courtNumber: '',
      courtName: '',
      judgeName: ''
    });
    onClose();
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="max-sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Converter Caso em Processo Judicial</DialogTitle>
          <DialogDescription>
            O caso será atualizado com informações do processo judicial
          </DialogDescription>
        </DialogHeader>
      {caseData && (
        <div className="rounded-lg border bg-muted/40 p-3">
          <div className="flex items-start gap-3">
            <Briefcase className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <h4 className="mb-1 font-medium">Caso de Origem</h4>
              <p className="text-sm text-muted-foreground">
                <strong>Título:</strong> {caseData.title}
              </p>
              {caseData.description && (
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                  <strong>Descrição:</strong> {caseData.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border p-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Briefcase className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span>Caso</span>
          <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <Scale className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">Processo Judicial</span>
        </div>
      </div>

      <div role="note" className="rounded-lg border bg-muted/40 p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-medium mb-1">Importante:</p>
            <ul className="list-inside list-disc space-y-1 text-muted-foreground">
              <li>O caso será marcado como &quot;Convertido em Processo Judicial&quot;</li>
              <li>Não aparecerá mais na lista padrão de casos</li>
              <li>Aparecerá na seção &quot;Processos Judiciais&quot;</li>
              <li>Todas as informações do caso serão mantidas</li>
            </ul>
          </div>
        </div>
      </div>

      <form id="convert-case-to-legal-process-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="convert-court-number" className="mb-1.5">
            Número do Processo Judicial <span className="text-destructive">*</span>
          </Label>
          <Input
            id="convert-court-number"
            value={formData.courtNumber}
            onChange={(e) => handleChange('courtNumber', e.target.value)}
            placeholder="Ex: 0000000-00.0000.0.00.0000"
            required
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Número oficial do processo no tribunal
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="convert-court-name" className="mb-1.5">Nome do Tribunal</Label>
            <Input
              id="convert-court-name"
              value={formData.courtName}
              onChange={(e) => handleChange('courtName', e.target.value)}
              placeholder="Ex: Tribunal de Justiça de SP"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Opcional - pode ser preenchido depois
            </p>
          </div>

          <div>
            <Label htmlFor="convert-judge-name" className="mb-1.5">Nome do Juiz</Label>
            <Input
              id="convert-judge-name"
              value={formData.judgeName}
              onChange={(e) => handleChange('judgeName', e.target.value)}
              placeholder="Ex: Dr. João da Silva"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Opcional - pode ser preenchido depois
            </p>
          </div>
        </div>

        <div className="rounded-lg border p-3">
          <div className="flex items-start gap-3">
            <Scale className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div>
              <h4 className="mb-1 font-medium">Após a conversão:</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• O caso será atualizado com os dados do processo judicial</li>
                <li>• Status alterado para &quot;Convertido em Processo Judicial&quot;</li>
                <li>• Evento registrado na timeline do cliente</li>
                <li>• Badge de origem &quot;De Caso&quot; será exibido</li>
                <li>• Aparecerá na aba &quot;Processos Judiciais&quot;</li>
              </ul>
            </div>
          </div>
        </div>
      </form>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="convert-case-to-legal-process-form"
            disabled={loading || !formData.courtNumber.trim()}
          >
            {loading ? 'Convertendo...' : 'Converter em Processo Judicial'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
